import { stringify } from 'query-string';

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import { useMenuScopeIdentifier } from 'hooks/menuScope';

import Box from '@mui/material/Box';

import TableMenus from 'components/MenuMakerResumeTable';
import DisplayMessage from 'components/DisplayMessageV2';
import PageError from 'components/PageError';
import HeaderForm from 'components/HeaderForm';

import { drawerActions, drawerEntities } from 'utils/drawerMenuMaker';
import { MENU_SCOPE } from 'utils/menuMaker/constants';
import { getScopeBasedTranslationKeys } from 'utils/menuMaker/scopeTranslations';

import TitleSection from 'components/TitleSection';
import { useCommandBarMenus } from 'hooks/useCommandBarAttributes';
import { MenuMakerImport } from 'components/MenuMakeImport';

import { EmptyMenus } from 'containers/MenuMakerResume/EmptyMenus';

import { useImportMenuModal } from 'components/MenuMakeImport/hooks';

import HelperSkeleton from './HelperSkeleton';

import useStyles from './styles';

/**
 * ResumeContent Component
 * Displays the main content for menu maker resume with menu list and controls
 * Includes functionality to create new menus with scope-based restrictions
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isSearchFilter - Whether search filter is active
 * @param {boolean} props.loading - Whether data is loading
 * @param {*} props.menusError - Error state for menus loading
 * @param {Array} props.menus - Array of menu objects
 * @param {Function} props.onPaginate - Function to handle pagination
 * @param {boolean} props.openDrawer - Whether a drawer is currently open
 * @param {Object} props.menusPagination - Pagination data for menus
 * @param {Object} props.paginationState - Current pagination state
 * @param {Function} props.onSearchMenus - Function to handle menu search
 * @returns {React.ReactElement} Resume content component
 */
function ResumeContent({
  isSearchFilter,
  loading,
  menusError,
  menus,
  onPaginate,
  openDrawer,
  menusPagination,
  paginationState,
  onSearchMenus,
  onLoadMenus,
}) {
  const { importMenuModalIsOpen } = useImportMenuModal();
  const history = useHistory();
  const { modeDraft, isPublishMenuError } = useSidebarLayout();
  const { isStoreMode, isOrganizationScope } = useMenuScopeIdentifier();
  const classes = useStyles({ openDrawer, isModeDraft: modeDraft || isPublishMenuError });
  const { t } = useTranslation('menuMaker');
  const commandBarHook = useCommandBarMenus();

  const scrollPaginationRef = useRef(null);

  // Show "Add Menu" functionality only when NOT in restrictive store mode
  const shouldShowAddMenu = !isStoreMode;

  // Get scope-based translation keys
  const { titleKey, descriptionKey } = getScopeBasedTranslationKeys(isOrganizationScope, 'resume');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const { isIntersecting } = entries[0];
        if (isIntersecting && menusPagination?.next) {
          if (!paginationState.fetching) {
            onPaginate();
          }
        }
      },
      { threshold: 0 },
    );

    if (scrollPaginationRef.current) observer.observe(scrollPaginationRef.current);

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPaginationRef, menusPagination?.next]);

  const handleOpenNewMenu = () => {
    const query = { action: drawerActions.CREATE, entity: drawerEntities.MENU };

    history.replace({ search: stringify(query) });
  };

  function renderContent() {
    if (loading) {
      return <HelperSkeleton />;
    }

    if (menusError) {
      return (
        <Box className={classes.containerMessageError}>
          <PageError
            data-testid="resumeMenusLoadError"
            id="resumeMenusLoadError"
            labelAction={t('common:buttons.retry')}
            maxWidth={300}
            message={t('common:errors.loaded.errorConnection')}
            onAction={onSearchMenus}
            title={t('common:errors.sorry')}
          />
        </Box>
      );
    }

    if (!menus?.length && !loading && isSearchFilter) {
      return (
        <DisplayMessage
          data-testid="menusSearchNoData"
          id="menusSearchNoData"
          maxWidth={450}
          message={t('common:messages.searchMessageNoResults')}
          sizeIcon={120}
          title={t('common:messages.searchTitleNoResults')}
        />
      );
    }

    if (!loading && menus?.length === 0 && !isSearchFilter) {
      return <EmptyMenus />;
    }

    return (
      <TableMenus
        loadingPagination={paginationState.fetching}
        menus={menus}
        openDrawer={openDrawer}
        scrollPaginationRef={scrollPaginationRef}
      />
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.contentResume}>
        <TitleSection
          commandBarHook={commandBarHook}
          description={<Trans i18nKey={descriptionKey} />}
          title={t(titleKey)}
        />
        <HeaderForm
          addButtonLabel={shouldShowAddMenu ? t('menuMaker:buttons.newMenu') : undefined}
          isShowFilterBrands
          onAddButtonClick={shouldShowAddMenu ? handleOpenNewMenu : undefined}
          onSearchBoxChange={onSearchMenus}
          openDrawer={openDrawer}
          px={4}
          searchLabel={t('menuMaker:headerForm.searchMenu')}
        />

        {renderContent()}
        {importMenuModalIsOpen && <MenuMakerImport onSuccess={onLoadMenus} />}
      </Box>
    </Box>
  );
}

ResumeContent.propTypes = {
  isSearchFilter: PropTypes.bool,
  loading: PropTypes.bool,
  menusError: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool]),
  menus: PropTypes.array,
  onSearchMenus: PropTypes.func,
  onPaginate: PropTypes.func,
  openDrawer: PropTypes.bool,
  menusPagination: PropTypes.object,
  paginationState: PropTypes.object,
};

export default ResumeContent;
